import { API_BASE_URL, ENDPOINTS } from "../config/api";

export interface AuthUser {
  id?: string;
  email?: string;
  name?: string;
  phone?: string;
  role?: string;
  phoneVerified?: boolean;
  emailVerified?: boolean;
  photos?: { url: string }[];
  [key: string]: any;
}

export function getUser(): AuthUser | null {
  try {
    const raw = localStorage.getItem("user");
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function isLoggedIn(): boolean {
  return !!localStorage.getItem("accessToken") && !!getUser();
}

export function loginUser(data: {
  accessToken: string;
  refreshToken: string;
  user: AuthUser;
}) {
  localStorage.setItem("accessToken", data.accessToken);
  localStorage.setItem("refreshToken", data.refreshToken);
  localStorage.setItem("user", JSON.stringify(data.user));
  window.dispatchEvent(new Event("authChange"));
}

export function logoutUser() {
  localStorage.removeItem("accessToken");
  localStorage.removeItem("refreshToken");
  localStorage.removeItem("user");
  window.dispatchEvent(new Event("authChange"));
}

/**
 * Called when the session is unrecoverable (refresh failed or retry still 401).
 * Clears storage, notifies listeners, and broadcasts `auth:expired` so the
 * app can force a redirect to the login page.
 */
export function expireSession() {
  const hadToken = !!localStorage.getItem("accessToken");
  localStorage.removeItem("accessToken");
  localStorage.removeItem("refreshToken");
  localStorage.removeItem("user");
  window.dispatchEvent(new Event("authChange"));
  // Only fire redirect if there was actually a session — avoids loops on
  // pages that fire anonymous requests (e.g. public previews).
  if (hadToken) {
    window.dispatchEvent(new Event("auth:expired"));
  }
}

/* ── Token refresh (single-flight) ────────────────────────────────── */

let refreshInFlight: Promise<string | null> | null = null;

async function refreshAccessToken(): Promise<string | null> {
  if (refreshInFlight) return refreshInFlight;

  refreshInFlight = (async () => {
    const refreshToken = localStorage.getItem("refreshToken");
    if (!refreshToken) return null;

    try {
      const res = await fetch(`${API_BASE_URL}${ENDPOINTS.REFRESH}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refreshToken }),
      });

      if (!res.ok) return null;

      const raw = await res.json().catch(() => null);
      const payload = raw?.data ?? raw;
      const newAccess: string | undefined = payload?.accessToken;
      const newRefresh: string | undefined = payload?.refreshToken;

      if (!newAccess) return null;

      localStorage.setItem("accessToken", newAccess);
      if (newRefresh) localStorage.setItem("refreshToken", newRefresh);
      return newAccess;
    } catch {
      return null;
    } finally {
      refreshInFlight = null;
    }
  })();

  return refreshInFlight;
}

/* ── Authenticated fetch ──────────────────────────────────────────── */

/**
 * Authenticated fetch:
 * - 10s default timeout
 * - Retries once on 401 after refreshing the access token
 * - On unrecoverable 401 (refresh failed, or retry also 401'd),
 *   calls expireSession() → app-wide redirect to login
 */
export async function authFetch(
  url: string,
  options: RequestInit = {},
  timeoutMs = 10000,
  _isRetry = false,
): Promise<Response> {
  const token = localStorage.getItem("accessToken");
  const headers: HeadersInit = {
    "Content-Type": "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(options.headers || {}),
  };

  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), timeoutMs);

  let res: Response;
  try {
    res = await fetch(url, { ...options, headers, signal: controller.signal });
  } finally {
    clearTimeout(timeout);
  }

  if (res.status === 401) {
    if (!_isRetry) {
      const newToken = await refreshAccessToken();
      if (newToken) {
        return authFetch(url, options, timeoutMs, true);
      }
    }
    // Either refresh failed, or retry also 401'd — session is dead
    expireSession();
  }

  return res;
}

export async function safeJson(res: Response | null): Promise<any> {
  if (!res) return null;
  try {
    const text = await res.text();
    if (!text) return null;
    return JSON.parse(text);
  } catch {
    return null;
  }
}
